import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import { useRealtime } from "./realtime";
import type { Message, RealtimePayload } from "./types";
import { useVoice } from "./voice";

type DesktopNotification = { title: string; body: string; route?: string };
type CallAction = "accept" | "decline";
type DesktopUpdateState = { status: string; currentVersion?: string; latestVersion?: string; error?: string };
export type DesktopCaptureSource = { id: string; name: string; displayId?: string; thumbnailDataUrl?: string };

export type LiveControllerState = { sourceLabel: string; qualityLabel: string; viewerCount: number; bitrateLabel?: string; statusLabel?: string; audioLabel?: string; computerAudioAvailable?: boolean; computerAudioEnabled?: boolean; computerAudioVolume?: number; microphoneAvailable?: boolean; microphoneEnabled?: boolean; microphoneVolume?: number };
export type LiveControllerAction = "change-source" | "show-controls" | "stop" | "computer-volume" | "microphone-volume";
export type LiveControllerCommand = { action: LiveControllerAction; value?: number };
export type ShareQuality = "auto" | "480p" | "720p";
export type SharePickerSelection = { source: DesktopCaptureSource; quality: ShareQuality; includeComputerAudio: boolean; computerAudioVolume: number; includeMicrophone: boolean; microphoneVolume: number };
type DisplayCapturePrepareResult = { ok: boolean; error?: string };
type VerseDesktopBridge = {
  openLiveViewer: (payload: { streamId: string; chatRoute: string }) => void;
  closeLiveViewer: () => void;
  minimizeLiveViewer: () => void;
  toggleMaximizeLiveViewer: () => void;
  showShareController: (state?: LiveControllerState) => void;
  updateShareController: (state: LiveControllerState) => void;
  setScreenShareActive: (active: boolean) => void;
  hideShareController: () => void;
  minimizeShareController: () => void;
  expandShareController: () => void;
  moveShareController: (delta: { dx: number; dy: number }) => void;
  getLiveControllerState: () => Promise<LiveControllerState | null>;
  sendLiveControllerAction: (action: LiveControllerAction, value?: number) => void;
  onLiveControllerAction: (callback: (command: LiveControllerCommand) => void) => () => void;
  onLiveControllerState: (callback: (state: LiveControllerState) => void) => () => void;
  onLiveControllerMinimized: (callback: (value: boolean) => void) => () => void;
  showSharePicker: () => void;
  closeSharePicker: () => void;
  submitSharePicker: (selection: SharePickerSelection) => void;
  onSharePickerAction: (callback: (selection: SharePickerSelection) => void) => () => void;
  isDesktop: true;
  platform: string;
  notify: (payload: DesktopNotification) => void;
  showIncomingCall: (payload: { name: string; subtitle: string }) => void;
  updateCallState: (payload: { state: string }) => void;
  setBadgeCount: (count: number) => void;
  showMainWindow: (route?: string) => void;
  sendCallAction: (action: CallAction) => void;
  loginWithGoogle: () => Promise<string>;
  getCaptureSources: () => Promise<DesktopCaptureSource[]>;
  prepareDisplayCapture: (sourceId: string) => Promise<DisplayCapturePrepareResult>;
  logScreenShareStage: (payload: { stage: string; sourceKind?: string; detail?: string }) => void;
  getUpdateState: () => Promise<DesktopUpdateState>;
  installUpdate: () => void;
  onUpdateState: (callback: (state: DesktopUpdateState) => void) => () => void;
  onCallAction: (callback: (action: CallAction) => void) => () => void;
  onNavigate: (callback: (route: string) => void) => () => void;
};

declare global {
  interface Window { verseDesktop?: VerseDesktopBridge }
}

function messageRoute(payload: RealtimePayload, message?: Message) {
  if (payload.type === "dm_message") {
    const username = message?.from?.username || message?.username || String(payload.fromUsername || "");
    return username ? `/dm/${encodeURIComponent(username)}` : "/inbox";
  }
  const slug = message?.channelSlug || String(payload.channelSlug || payload.channel || "");
  return slug ? `/channel/${encodeURIComponent(slug)}` : "/comms";
}

function isRouteOpen(pathname: string, route: string) {
  return decodeURIComponent(pathname).replace(/\/+$/, "") === decodeURIComponent(route).replace(/\/+$/, "");
}

function notificationFor(payload: RealtimePayload): DesktopNotification | null {
  const message = payload.message;
  if (payload.type === "dm_message") {
    const name = message?.from?.displayName || message?.displayName || message?.from?.username || message?.username || String(payload.fromDisplayName || payload.fromUsername || "Pilot");
    return { title: name, body: message?.text || "Sent you a message", route: messageRoute(payload, message) };
  }
  if (payload.type === "channel_message") {
    const name = message?.displayName || message?.username || "Pilot";
    const channel = message?.channelSlug || String(payload.channelSlug || payload.channel || "crew channel");
    return { title: `${name} in #${channel}`, body: message?.text || "New channel message", route: messageRoute(payload, message) };
  }
  const eventCopy: Record<string, [string, string, string]> = {
    friend_request: ["New crew request", "A pilot sent you a friend request.", "/crew"],
    friend_activity: ["Crew activity", "New activity from your crew.", "/home"],
    marketplace_message: ["Marketplace message", "A pilot replied to a listing.", "/market"],
    marketplace_interest: ["Marketplace interest", "A pilot is interested in your listing.", "/market"],
    notification: [String(payload.title || "VerseComms"), String(payload.body || payload.message || "New transmission"), String(payload.route || "/home")]
  };
  const copy = eventCopy[payload.type];
  return copy ? { title: copy[0], body: copy[1], route: copy[2] } : null;
}

export function DesktopRuntime() {
  const bridge = window.verseDesktop;
  const auth = useAuth();
  const realtime = useRealtime();
  const voice = useVoice();
  const location = useLocation();
  const navigate = useNavigate();
  const pathnameRef = useRef(location.pathname);
  const userIdRef = useRef(auth.user?.id);
  const lastIncomingRef = useRef("");
  const ringbackRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { userIdRef.current = auth.user?.id; }, [auth.user?.id]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.onNavigate(route => navigate(route));
  }, [bridge, navigate]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.onCallAction(action => {
      if (action === "accept") void voice.acceptIncomingCall();
      else voice.rejectIncomingCall("declined");
    });
  }, [bridge, voice.acceptIncomingCall, voice.rejectIncomingCall]);

  useEffect(() => {
    if (!bridge || !auth.user) return;
    return realtime.subscribe(payload => {
      if (payload.type === "call_invite") return;
      const message = payload.message;
      const senderId = message?.userId || message?.fromUserId || String(payload.fromUserId || "");
      if (senderId && senderId === userIdRef.current) return;
      const notification = notificationFor(payload);
      const viewingConversation = notification?.route && isRouteOpen(pathnameRef.current, notification.route);
      if (!notification || (viewingConversation && !document.hidden && document.hasFocus())) return;
      bridge.notify(notification);
    });
  }, [auth.user, bridge, realtime]);

  useEffect(() => {
    if (!bridge) return;
    if (voice.incoming) {
      if (lastIncomingRef.current === voice.incoming.callId) return;
      lastIncomingRef.current = voice.incoming.callId;
      bridge.showIncomingCall({
        name: voice.incoming.fromDisplayName || voice.incoming.fromUsername || "Pilot",
        subtitle: "Incoming VerseComms voice call"
      });
      return;
    }
    if (lastIncomingRef.current && !voice.session) bridge.updateCallState({ state: "ended" });
    lastIncomingRef.current = "";
  }, [bridge, voice.incoming, voice.session]);

  useEffect(() => {
    if (!bridge) return;
    bridge.updateCallState({ state: voice.session?.status || "ended" });
    const outgoingRinging = voice.session?.direction === "outgoing" && voice.session.status === "ringing";
    if (outgoingRinging) {
      const audio = new Audio("/sounds/desktop-ringback.wav");
      audio.loop = true;
      audio.volume = 0.55;
      ringbackRef.current = audio;
      void audio.play().catch(() => undefined);
    }
    return () => {
      ringbackRef.current?.pause();
      ringbackRef.current = null;
    };
  }, [bridge, voice.session?.direction, voice.session?.status]);

  return null;
}

export function isDesktopApp() {
  return Boolean(window.verseDesktop?.isDesktop);
}
